import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../operator/operatorConstants';
import { SCAN_SOURCE } from '../operator/operatorScanGate';
import { asSentence } from '../operator/operatorUtils';

/*
 * The load that goes up the truck, as the floor builds it. An order goes up by
 * scanning its items («bultos»), and only an order with every item scanned is
 * complete: only complete orders leave, and whole.
 *
 * The load keeps, by order: its row as the server last sent it, how its items
 * are read (`match`), the items scanned or counted, the first code read for it
 * and, after a dispatch that failed it, why. `orderIds` keeps the order last
 * touched first.
 */

/** How a label read at dispatch matched its order: one item's, or the one of the whole shipment. */
export const LABEL_MATCH = { ITEM: 'item', SHIPMENT: 'shipment' };

/** What a read did to the load. */
export const SCAN_OUTCOME = {
  /** An item of the order went up. */
  ITEM_ADDED: 'item_added',
  /** That item was already up: it adds nothing. */
  ITEM_REPEATED: 'item_repeated',
  /** The shipment label counted one more item. */
  SHIPMENT_COUNTED: 'shipment_counted',
  /** Every item of the order is counted already. */
  SHIPMENT_FULL: 'shipment_full',
  /** The camera saw the shipment label of an order already up: it never counts twice. */
  SHIPMENT_CAMERA: 'shipment_camera',
  /** The other kind of label of an order already being read one way. */
  OTHER_LABEL: 'other_label',
  /** A tapped order went up whole, its items not scanned. */
  ORDER_LOADED: 'order_loaded',
  /** A tapped order had every item up already. */
  ORDER_FULL: 'order_full',
};

/** What a dispatch said of each order. */
export const DISPATCH_OUTCOME = {
  DISPATCHED: 'dispatched',
  ALREADY: 'already_dispatched',
  FAILED: 'failed',
};

/** How a failed order stands once it was read again; see `retryFailedDispatch`. */
export const RECHECK = {
  /** Unchanged and still for dispatch: complete again. */
  READY: 'ready',
  /** Its items changed: its scans start over. */
  RESCAN: 'rescan',
  /** The server will not dispatch it now. */
  REFUSED: 'refused',
  /** The read got no answer: it stays as it was. */
  LOST: 'lost',
};

export const MIXED_COURIERS_TEXT = 'La carga mezcla couriers';

/** How many orders one load takes, as the server does. */
export const MAX_LOAD_ORDERS = 100;

/** What the primary says while nothing can leave. */
export const NOTHING_TO_DISPATCH_TEXT = 'Escanea los bultos que suben al camión';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

export const ordersText = (count) => plural(count, 'orden', 'órdenes');

export const itemsText = (count) => plural(count, 'bulto', 'bultos');

/** «1 bulto escaneado», «3 bultos escaneados». */
export const scannedItemsText = (count) => plural(count, 'bulto escaneado', 'bultos escaneados');

/** An order as the floor names it: «ORD-1», or its number. */
export const dispatchOrderName = (row) => row?.reference_id || `Orden #${row?.id}`;

export const courierLabelOf = (row) => row?.courier?.label || 'Sin courier';

/** The courier's code, `''` without one. */
export const courierKeyOf = (row) => row?.courier?.code ?? '';

/** An order as it goes up the load, before anything of it is scanned. */
export const emptyEntry = (row, match, code) => ({
  row,
  match,
  code,
  scannedItemIds: [],
  shipmentCount: 0,
  error: null,
});

/** How many items the order has. */
export const itemsTotalOf = (entry) => Math.max(Number(entry?.row?.items_count) || 0, 0);

/** How many of its items are up: those scanned that it still has, or those counted under its one label. */
export const scannedCountOf = (entry) => {
  if (!entry) return 0;
  if (entry.match === LABEL_MATCH.SHIPMENT) return Math.min(entry.shipmentCount, itemsTotalOf(entry));
  const ids = new Set((entry.row?.items || []).map((item) => item.id));
  return entry.scannedItemIds.filter((id) => ids.has(id)).length;
};

/**
 * Whether the order can leave: every item up, still for dispatch, and not
 * failed by the last dispatch until it is read again.
 */
export const isOrderComplete = (entry) => {
  if (!entry || entry.error || entry.row?.dispatchable === false) return false;
  const total = itemsTotalOf(entry);
  return total > 0 && scannedCountOf(entry) === total;
};

/** «2 de 3 bultos». */
export const progressText = (entry) => `${scannedCountOf(entry)} de ${itemsText(itemsTotalOf(entry))}`;

/** «1 de 3 bultos escaneados», «1 de 1 bulto escaneado». */
export const scannedProgressText = (entry) =>
  `${scannedCountOf(entry)} de ${scannedItemsText(itemsTotalOf(entry))}`;

/** What taking an order off the load asks for, once some of its items are up. */
export const takeOffLoadText = (entry) => {
  const down = scannedCountOf(entry) === 1 ? 'Bájalo del camión: no sale' : 'Bájalos del camión: no salen';
  return `Ya tiene ${scannedProgressText(entry)}. ${down} con esta carga.`;
};

/** The load's orders, the one last touched first. */
export const loadEntriesOf = (load) => (load?.orderIds || []).map((id) => load.orders[id]).filter(Boolean);

/** The couriers of some orders, by name, with how many orders and items each. */
export const couriersOf = (entries) => {
  const byCode = new Map();
  entries.forEach((entry) => {
    const code = courierKeyOf(entry.row);
    const courier = byCode.get(code) || { code: code || null, label: courierLabelOf(entry.row), orders: 0, items: 0 };
    courier.orders += 1;
    courier.items += itemsTotalOf(entry);
    byCode.set(code, courier);
  });
  return [...byCode.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
};

/**
 * The load in numbers: what is complete and leaves, what is not and stays,
 * what failed the last dispatch, the items that leave and their couriers.
 * `mixedCouriers` looks at every order up, complete or not: they are all on
 * the truck.
 *
 * @param {{orderIds: number[], orders: object}} load
 */
export const loadSummary = (load) => {
  const entries = loadEntriesOf(load);
  const complete = entries.filter(isOrderComplete);
  const failed = entries.filter((entry) => entry.error);
  const incomplete = entries.filter((entry) => !entry.error && !isOrderComplete(entry));
  return {
    entries,
    complete,
    incomplete,
    failed,
    items: complete.reduce((total, entry) => total + itemsTotalOf(entry), 0),
    couriers: couriersOf(complete),
    allCouriers: couriersOf(entries),
    mixedCouriers: couriersOf(entries).length > 1,
  };
};

/** The primary: «Despachar 8 órdenes · 11 bultos», or what is missing. */
export const submitLabel = (summary) =>
  summary.complete.length === 0
    ? NOTHING_TO_DISPATCH_TEXT
    : `Despachar ${ordersText(summary.complete.length)} · ${itemsText(summary.items)}`;

/** The couriers by name: «Chilexpress y Starken». */
export const couriersText = (couriers) => {
  const labels = couriers.map((courier) => courier.label);
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}` : labels[0] || '';
};

/**
 * What stays behind when the load leaves: «Quedan fuera 2 órdenes incompletas:
 * ORD-3 (1 de 3 bultos) y ORD-4 (0 de 2 bultos).», or `null`.
 */
export const leftOutText = (incomplete) => {
  if (incomplete.length === 0) return null;
  const names = incomplete.map((entry) => `${dispatchOrderName(entry.row)} (${progressText(entry)})`);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}` : names[0];
  return `Quedan fuera ${plural(incomplete.length, 'orden incompleta', 'órdenes incompletas')}: ${list}.`;
};

/**
 * What the dispatch sends: the complete orders, each with the items scanned
 * or the count under its one label. Sorted, so the same load is the same
 * action whatever order it was scanned in, and a lost answer goes again under
 * its key.
 */
export const dispatchArgOf = (summary) => ({
  orders: summary.complete
    .map((entry) => {
      if (entry.match === LABEL_MATCH.SHIPMENT) return { orderId: entry.row.id, itemsCount: itemsTotalOf(entry) };
      const ids = new Set((entry.row.items || []).map((item) => item.id));
      return {
        orderId: entry.row.id,
        itemIds: entry.scannedItemIds.filter((id) => ids.has(id)).sort((a, b) => a - b),
      };
    })
    .sort((a, b) => a.orderId - b.orderId),
});

/** The body of `POST W/dispatch/`. */
export const buildDispatchBody = (arg, key) => ({
  orders: arg.orders.map(({ orderId, itemIds, itemsCount }) =>
    itemIds ? { order_id: orderId, item_ids: itemIds } : { order_id: orderId, items_count: itemsCount }
  ),
  idempotency_key: key,
});

/** Whether the order's items changed since its row was read: another count, or other items. */
export const rowChanged = (before, after) => {
  if (!before || !after) return false;
  if (Number(before.items_count) !== Number(after.items_count)) return true;
  const ids = (row) =>
    (row.items || [])
      .map((item) => item.id)
      .sort((a, b) => a - b)
      .join(',');
  return ids(before) !== ids(after);
};

/**
 * What a label read does to the load, from the scan's answer and the order as
 * the load has it. An order whose items changed since it went up starts over
 * — the server would refuse the old count — and this read counts on it. Each
 * item's label adds that item once. The shipment label counts one item a
 * read, never past the order's items, and the camera only counts it the first
 * time: a box resting in front of it must not add up.
 *
 * @param {object|undefined} entry - The order in the load, if it is up.
 * @param {{order: object, item_id: number|null, match: string}} answer
 * @param {{code: string, source: string}} read
 * @returns {{kind: string, changed: boolean, entry: object}} The order as the read leaves it.
 */
export const scanOutcome = (entry, answer, { code, source }) => {
  const row = answer.order;
  const match = answer.match === LABEL_MATCH.ITEM && answer.item_id != null ? LABEL_MATCH.ITEM : LABEL_MATCH.SHIPMENT;
  const changed = Boolean(entry) && rowChanged(entry.row, row);
  const base = !entry || changed ? emptyEntry(row, match, code) : { ...entry, row };

  if (base.match !== match) return { kind: SCAN_OUTCOME.OTHER_LABEL, changed, entry: base };

  if (match === LABEL_MATCH.ITEM) {
    if (base.scannedItemIds.includes(answer.item_id)) return { kind: SCAN_OUTCOME.ITEM_REPEATED, changed, entry: base };
    return {
      kind: SCAN_OUTCOME.ITEM_ADDED,
      changed,
      entry: { ...base, scannedItemIds: [...base.scannedItemIds, answer.item_id] },
    };
  }

  if (base.shipmentCount >= itemsTotalOf(base)) return { kind: SCAN_OUTCOME.SHIPMENT_FULL, changed, entry: base };
  if (source === SCAN_SOURCE.CAMERA && base.shipmentCount > 0) {
    return { kind: SCAN_OUTCOME.SHIPMENT_CAMERA, changed, entry: base };
  }
  return { kind: SCAN_OUTCOME.SHIPMENT_COUNTED, changed, entry: { ...base, shipmentCount: base.shipmentCount + 1 } };
};

/** Whether a read, or a tapped order, put something more up the load. */
export const scanAdds = (outcome) =>
  [SCAN_OUTCOME.ITEM_ADDED, SCAN_OUTCOME.SHIPMENT_COUNTED, SCAN_OUTCOME.ORDER_LOADED].includes(outcome.kind);

/**
 * Whether tapping an order of the queue offers to put it up the load whole:
 * still for dispatch, with items, and not already up whole or failed — a
 * failed one goes back through «Reintentar las que fallaron».
 */
export const canLoadByTap = (row, entry) =>
  Boolean(row) &&
  row.dispatchable !== false &&
  (row.items?.length ?? 0) > 0 &&
  (!entry || (!entry.error && !isOrderComplete(entry)));

/**
 * Where a blocked order gets its label from the floor: its packing screen,
 * which sends it to its courier when it has none (`missing`) and creates its
 * shipment again once the floor changed its items (`stale`). `null` for a
 * label still on its way (`pending`) or short of some items (`mismatch`):
 * sending again mends neither. Only where packing is open and the backend
 * does it from the floor.
 *
 * @param {object} row - A row of the queue: `dispatchable` and `labels.status`.
 * @param {object} context
 * @param {string[]} context.flows
 * @param {boolean} context.courierSend
 * @param {boolean} context.courierRelabel
 * @returns {{to: string, label: string}|null}
 */
export const labelFixOf = (row, { flows, courierSend, courierRelabel }) => {
  if (row?.dispatchable !== false || !flows.includes(OPERATOR_FLOW.PACKING)) return null;
  const status = row.labels?.status;
  const fixes = (status === 'missing' && courierSend) || (status === 'stale' && courierRelabel);
  return fixes ? { to: `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PACKING]}/${row.id}`, label: 'Generar etiqueta' } : null;
};

/**
 * What a confirmed tap does to the load: every item of the order up, as the
 * server has it now. An order already up keeps how it was being read — item
 * by item, or counted under its one label —; one new to the load, or whose
 * items changed, goes item by item, by the code it was read again with.
 *
 * @param {object|undefined} entry - The order in the load, if it is up.
 * @param {object} row - The order as the server sent it again.
 * @param {string} code - What it was read again with: its first item's id.
 * @returns {{kind: string, changed: boolean, added: number, entry: object}}
 */
export const loadOrderOutcome = (entry, row, code) => {
  const changed = Boolean(entry) && rowChanged(entry.row, row);
  const base = !entry || changed ? emptyEntry(row, LABEL_MATCH.ITEM, code) : { ...entry, row };
  const total = itemsTotalOf(base);
  const before = scannedCountOf(base);
  if (before >= total) return { kind: SCAN_OUTCOME.ORDER_FULL, changed, added: 0, entry: base };
  const loaded =
    base.match === LABEL_MATCH.SHIPMENT
      ? { ...base, shipmentCount: total }
      : { ...base, scannedItemIds: (row.items || []).map((item) => item.id) };
  return { kind: SCAN_OUTCOME.ORDER_LOADED, changed, added: total - before, entry: loaded };
};

/**
 * What the sheet asks before a tapped order goes up: which items, which of
 * them are up already, and that the rest go without being scanned.
 *
 * @param {object} row - The order of the queue.
 * @param {object|undefined} entry - The order in the load, if it is up.
 * @returns {{title: string, message: string, items: object[], confirmLabel: string}}
 */
export const loadOfferSheet = (row, entry) => {
  const name = dispatchOrderName(row);
  const total = Math.max(Number(row.items_count) || 0, 0);
  const up = entry ? scannedCountOf(entry) : 0;
  const missing = total - up;
  const scannedIds = entry?.match === LABEL_MATCH.ITEM ? entry.scannedItemIds : [];

  let message;
  let confirmLabel;
  if (up > 0) {
    message =
      missing === 1
        ? `Ya tiene ${up} de ${itemsText(total)} en la carga. Se sube el que falta sin escanearlo: revisa que esté en el camión.`
        : `Ya tiene ${up} de ${itemsText(total)} en la carga. Se suben los ${missing} que faltan sin escanearlos: revisa que estén en el camión.`;
    confirmLabel = missing === 1 ? 'Subir el que falta' : `Subir los ${missing} que faltan`;
  } else {
    message =
      total === 1
        ? 'Se sube su bulto sin escanearlo: revisa que esté en el camión.'
        : `Se suben sus ${itemsText(total)} sin escanearlos: revisa que estén en el camión.`;
    confirmLabel = total === 1 ? 'Subir el bulto' : `Subir los ${itemsText(total)}`;
  }

  return {
    title: `¿Subir ${name} a la carga?`,
    message,
    items: (row.items || []).map((item) => ({
      key: item.id,
      title: `Bulto ${item.item_index}`,
      subtitle: [`Etiqueta ${item.id}`, item.tracking_number, scannedIds.includes(item.id) ? 'ya escaneado' : null]
        .filter(Boolean)
        .join(' · '),
    })),
    confirmLabel,
  };
};

/**
 * The band of a tapped order: «ORD-70 completa: subiste sus 3 bultos sin
 * escanearlos.» An order whose items changed says so first.
 *
 * @param {{kind: string, changed: boolean, added: number, entry: object}} outcome
 */
export const loadOrderMessage = ({ kind, changed, added, entry }) => {
  const name = dispatchOrderName(entry.row);
  const total = itemsTotalOf(entry);
  if (kind === SCAN_OUTCOME.ORDER_FULL) return { tone: 'warning', message: `${name} ya tiene sus ${itemsText(total)} en la carga` };
  const before = changed ? `${name} cambió: ahora tiene ${itemsText(total)}. ` : '';
  let what;
  if (added === total) what = total === 1 ? 'subiste su bulto sin escanearlo' : `subiste sus ${itemsText(total)} sin escanearlos`;
  else what = added === 1 ? 'subiste el que faltaba sin escanearlo' : `subiste los ${added} que faltaban sin escanearlos`;
  return { tone: changed ? 'warning' : 'success', message: `${before}${name} completa: ${what}.` };
};

/**
 * The band of a read: «ORD-1 · bulto 2 de 3», «ORD-1 completa», «Ese bulto ya
 * está en la carga». An order whose items changed says so first.
 *
 * @param {{kind: string, changed: boolean, entry: object}} outcome
 */
export const scanMessage = ({ kind, changed, entry }) => {
  const name = dispatchOrderName(entry.row);
  const total = itemsTotalOf(entry);
  const scanned = scannedCountOf(entry);
  const before = changed ? `${name} cambió: ahora tiene ${itemsText(total)}. ` : '';
  switch (kind) {
    case SCAN_OUTCOME.ITEM_ADDED:
    case SCAN_OUTCOME.SHIPMENT_COUNTED:
      return scanned === total
        ? { tone: 'success', message: `${before}${name} completa` }
        : { tone: changed ? 'warning' : 'step', message: `${before}${name} · bulto ${scanned} de ${total}` };
    case SCAN_OUTCOME.ITEM_REPEATED:
      return { tone: 'warning', message: `Ese bulto ya está en la carga · ${name} ${scanned} de ${total}` };
    case SCAN_OUTCOME.SHIPMENT_FULL:
      return { tone: 'warning', message: `${name} ya tiene sus ${itemsText(total)} en la carga` };
    case SCAN_OUTCOME.SHIPMENT_CAMERA:
      return { tone: 'warning', message: `${name} ya está en la carga: suma sus otros bultos con «+1 bulto»` };
    default:
      return {
        tone: 'warning',
        message:
          entry.match === LABEL_MATCH.ITEM
            ? `${name} tiene una etiqueta por bulto: escanea la de cada uno`
            : `${name} se cuenta con la etiqueta del envío: suma sus bultos con «+1 bulto»`,
      };
  }
};

/**
 * The «+1» of an order counted under its one label, while it has items left
 * to count: «+1 bulto de ORD-1 (1 de 3)». `null` otherwise.
 *
 * @param {object|undefined} entry
 */
export const shipmentRepeatLabel = (entry) => {
  if (!entry || entry.error || entry.match !== LABEL_MATCH.SHIPMENT) return null;
  const total = itemsTotalOf(entry);
  const scanned = scannedCountOf(entry);
  return scanned < total ? `+1 bulto de ${dispatchOrderName(entry.row)} (${scanned} de ${total})` : null;
};

/** Why an order the scan found cannot go up: «ORD-9 no se puede despachar: Sin etiqueta.» */
export const blockedMessage = (row) =>
  `${dispatchOrderName(row)} no se puede despachar: ${asSentence(row.blocked_reason || 'está bloqueada')}`;

const isOut = (result) => result.outcome === DISPATCH_OUTCOME.DISPATCHED || result.outcome === DISPATCH_OUTCOME.ALREADY;

/** «7 de 8 despachadas». */
export const resultTitle = (result) => {
  const results = result?.results || [];
  const total = results.length;
  return `${results.filter(isOut).length} de ${total} ${total === 1 ? 'despachada' : 'despachadas'}`;
};

/** The orders a dispatch failed, with why. */
export const failedResults = (result) =>
  (result?.results || []).filter((item) => item.outcome === DISPATCH_OUTCOME.FAILED);

/** The band of a dispatch: green when every order left, amber when some failed. */
export const resultSignal = (result) => {
  const failed = failedResults(result).length;
  const title = resultTitle(result);
  return failed > 0
    ? { tone: 'warning', message: `${title} · ${plural(failed, 'falló', 'fallaron')}` }
    : { tone: 'success', message: title };
};

/**
 * How a failed order stands once read again, said of each: «ORD-3 sigue
 * completa: confírmala para despacharla.», «ORD-3 cambió: escanea de nuevo sus
 * 4 bultos.», or why the server will not take it.
 *
 * @param {Array<{status: string, name: string, row?: object, message?: string}>} checks
 */
export const recheckSignal = (checks) => {
  // The server's words already name the order («ORD-3 ya salió de la bodega»): it is not said twice.
  const named = (name, message) =>
    message.startsWith(name) ? asSentence(message) : `${name}: ${asSentence(message)}`;
  const sentences = checks.map((check) => {
    switch (check.status) {
      case RECHECK.READY:
        return `${check.name} sigue completa: confírmala para despacharla.`;
      case RECHECK.RESCAN:
        // An item gone says the order changed, not how many it has now.
        return check.itemGone
          ? `${check.name} cambió: escanea de nuevo sus bultos.`
          : `${check.name} cambió: escanea de nuevo sus ${itemsText(Number(check.row.items_count) || 0)}.`;
      case RECHECK.LOST:
        return `${named(check.name, check.message || 'Sin respuesta del servidor')} Vuelve a intentarlo.`;
      default:
        return named(check.name, check.message || '');
    }
  });
  const statuses = checks.map((check) => check.status);
  let tone = 'step';
  if (statuses.some((status) => status !== RECHECK.READY)) tone = 'warning';
  if (statuses.includes(RECHECK.REFUSED)) tone = 'error';
  return { tone, message: sentences.join(' ') };
};

/**
 * A failed order's code to read it again. One read item by item goes by the
 * item's code it was read with: its shipment's code would be turned down for
 * having a label per item. One read by its shipment's label goes by that.
 */
export const recheckCodeOf = (entry) =>
  (entry.match === LABEL_MATCH.ITEM
    ? entry.code || entry.row?.shipment_tracking_number
    : entry.row?.shipment_tracking_number || entry.code) || null;

/** Whether the station is working something out: a read, the dispatch, or reading the failed orders again. */
export const isDispatchBusy = (dispatchState) =>
  Boolean(dispatchState.scanning || dispatchState.pending || dispatchState.retrying);
